import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";

export type ITreeNode = {
  id: string | number;
  code: string;
  name: string;
  node_type?: string;
  system_key: string;
  children?: ITreeNode[];
  amounts: number[];
};

interface DateItem {
  date: string;
  label: string;
}

type Props = {
  data: any[];
  isLoading: boolean;
  columns: DateItem[];
};

const TrialBalanceTable: React.FC<Props> = ({
  data,
  isLoading,
  columns = [],
}) => {
  const { t: generalT } = useTranslation("general");

  const [expanded, setExpanded] = useState<Record<string | number, boolean>>(
    {},
  );

  const toggle = (id: string | number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderRows = (nodes: ITreeNode[], depth = 0): React.ReactNode[] => {
    return nodes.flatMap((node, index) => {
      const isOpen = expanded[node.id];
      const hasChildren = node.children && node.children?.length > 0;

      return [
        <TableRow
          key={`${node.id}_${node.system_key}_${index}`}
          hover
          sx={{
            bgcolor:
              node.node_type === "category" ? "primary.light" : "transparent",
          }}
        >
          <TableCell width={80}>{node.code}</TableCell>

          {/* only this column has hierarchy */}
          <TableCell size="small">
            <Box display="flex" alignItems="center" pl={depth * 3}>
              {hasChildren && (
                <IconButton size="small" onClick={() => toggle(node.id)}>
                  {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              )}
              {!hasChildren && <Box width={32} />}
              {node.name}
            </Box>
          </TableCell>

          {/* these columns stay aligned */}
          {node.amounts.map((amount, i) => (
            <TableCell key={i} sx={{ fontWeight: 700 }}>
              {amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </TableCell>
          ))}
        </TableRow>,

        ...(hasChildren && isOpen ? renderRows(node.children!, depth + 1) : []),
      ];
    });
  };

  const tableHeaders = ["code", "accountName"];

  columns.forEach((col) => tableHeaders.push(col.label));

  return (
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: "primary.main" }}>
          {tableHeaders.map((item) => (
            <TableCell key={item}>
              <Typography
                fontSize={"12px"}
                textTransform={"uppercase"}
                color={"#fff"}
                lineHeight={"18px"}
                fontWeight={500}
              >
                {generalT(item)}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      {isLoading ? (
        <TableLoadingSkeleton rowsLength={5} cellsLength={4} />
      ) : (
        <TableBody>{renderRows(data)}</TableBody>
      )}
    </Table>
  );
};

export default TrialBalanceTable;
